import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { receivingLotService } from '@/services/receiving-lot.service';
import { EquipmentSearchCombobox } from '@/features/equipment-intake/EquipmentSearchCombobox';
import type { EquipmentSearchResult } from '@/services/equipment.service';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { generateUuid } from '@/lib/uuid';

interface AddArrivalDialogProps {
  lotId: string | null;
  onClose: () => void;
  onAdded: () => void;
}

/**
 * Manual, small-scale Add Arrival flow (search-and-add one equipment item at
 * a time, same combobox Equipment Intake already uses) — Phase 1c's Add
 * Arrival API is bounded at 500 items/request, matching Phase 1a's Lot
 * creation cap. This is not the future bulk-Excel-import channel (deferred,
 * see the multi-arrival readiness report §17) — it is the same class of
 * small/manual entry the Receiving Lot workspace already supports.
 */
export function AddArrivalDialog({ lotId, onClose, onAdded }: AddArrivalDialogProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<EquipmentSearchResult[]>([]);
  const [courierName, setCourierName] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [packageCount, setPackageCount] = useState('');
  const [remarks, setRemarks] = useState('');
  // Minted once per dialog-open (not per submit attempt) so a retry after a
  // network timeout/error reuses the same key — the server-side idempotency
  // guarantee only holds if the client actually resends the same key.
  const [idempotencyKey, setIdempotencyKey] = useState(() => generateUuid());

  const reset = () => {
    setSelected([]);
    setCourierName('');
    setTrackingNumber('');
    setPackageCount('');
    setRemarks('');
    setIdempotencyKey(generateUuid());
  };

  const addArrivalMutation = useMutation({
    mutationFn: () =>
      receivingLotService.addArrival(lotId!, {
        equipmentIds: selected.map((e) => e.id),
        courierName: courierName.trim() || undefined,
        trackingNumber: trackingNumber.trim() || undefined,
        packageCount: packageCount ? Number(packageCount) : undefined,
        remarks: remarks.trim() || undefined,
        idempotencyKey,
      }),
    onSuccess: () => {
      toast.success('Arrival recorded');
      queryClient.invalidateQueries({ queryKey: ['receiving-lot', lotId] });
      queryClient.invalidateQueries({ queryKey: ['receiving-lot-arrivals', lotId] });
      reset();
      onAdded();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to record arrival')),
  });

  const handleClose = () => {
    if (addArrivalMutation.isPending) return;
    reset();
    onClose();
  };

  const removeSelected = (id: string) => setSelected((prev) => prev.filter((e) => e.id !== id));

  return (
    <Modal open={lotId !== null} onClose={handleClose} title="Add Arrival" size="lg">
      <div className="space-y-4">
        <FormField label="Equipment in this delivery" htmlFor="add-arrival-equipment" required>
          <EquipmentSearchCombobox
            value=""
            onSelect={(result) => {
              if (result && !selected.some((e) => e.id === result.id)) {
                setSelected((prev) => [...prev, result]);
              }
            }}
            disabled={addArrivalMutation.isPending}
          />
        </FormField>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selected.map((e) => (
              <span
                key={e.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs text-foreground"
              >
                {e.assetNumber} · {e.serialNumber}
                <button
                  type="button"
                  onClick={() => removeSelected(e.id)}
                  disabled={addArrivalMutation.isPending}
                  aria-label={`Remove ${e.assetNumber}`}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Courier / carrier" htmlFor="add-arrival-courier">
            <Input
              id="add-arrival-courier"
              value={courierName}
              onChange={(e) => setCourierName(e.target.value)}
              disabled={addArrivalMutation.isPending}
              maxLength={255}
            />
          </FormField>
          <FormField label="Tracking / reference number" htmlFor="add-arrival-tracking">
            <Input
              id="add-arrival-tracking"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              disabled={addArrivalMutation.isPending}
              maxLength={255}
            />
          </FormField>
          <FormField label="Package count" htmlFor="add-arrival-packages">
            <Input
              id="add-arrival-packages"
              type="number"
              min={1}
              value={packageCount}
              onChange={(e) => setPackageCount(e.target.value)}
              disabled={addArrivalMutation.isPending}
            />
          </FormField>
        </div>

        <FormField label="Remarks" htmlFor="add-arrival-remarks">
          <Textarea
            id="add-arrival-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            maxLength={2000}
            disabled={addArrivalMutation.isPending}
          />
        </FormField>

        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={addArrivalMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => addArrivalMutation.mutate()}
            disabled={selected.length === 0 || addArrivalMutation.isPending}
          >
            {addArrivalMutation.isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
            )}
            Record Arrival
          </Button>
        </div>
      </div>
    </Modal>
  );
}
